import { useEffect, useState } from "react";
import { getPlatforms, connectPlatform, disconnectPlatform } from "../data/api.js";

const STATUS_LABEL = {
  connected:        { cls: "live",  label: "Connected" },
  disconnected:     { cls: "draft", label: "Not connected" },
  error:            { cls: "alert", label: "Error" },
  reauth_required:  { cls: "soon",  label: "Re-auth needed" },
};

export default function IntegrationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creds, setCreds] = useState({});
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await getPlatforms();
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const startConnect = (row) => {
    setOpenKey(row.key);
    setCreds({});
    setError(null);
  };

  const submitConnect = async (row) => {
    setBusy(row.key);
    setError(null);
    try {
      await connectPlatform(row.key, creds);
      setOpenKey(null);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const doDisconnect = async (row) => {
    setBusy(row.key);
    try {
      await disconnectPlatform(row.key);
      await refresh();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="integrations-wrap">
      <div className="pageh">
        <div>
          <h1>Integrations <em>— ticketing channels</em></h1>
          <div className="sub">
            Connect each platform once. EntryRate aggregates sales and
            inventory <b>read-only</b> by default · push-update privileges
            require partner-tier auth on each platform.
          </div>
        </div>
        <div className="pageh-actions">
          <button className="btn" onClick={refresh}>Refresh</button>
        </div>
      </div>

      {error && <div className="banner-error">{error}</div>}

      {loading ? (
        <div className="empty">Loading integrations…</div>
      ) : (
        <div className="integ-grid">
          {rows.map((row) => {
            const sl = STATUS_LABEL[row.status] || STATUS_LABEL.disconnected;
            const isOpen = openKey === row.key;
            const fields = row.credentialFields || [];
            const partnership = row.requiresPartnership;
            return (
              <div key={row.key} className={`integ-card${isOpen?" open":""}`}>
                <div className="integ-head">
                  <span className="pdot integ-logo" data-key={row.key}>{row.code}</span>
                  <div className="integ-name">
                    <div className="integ-title">{row.name}</div>
                    <div className="integ-meta">
                      <span className={`pill ${sl.cls}`}>{sl.label}</span>
                      {partnership && <span className="pill alert" title={row.why}>partnership required</span>}
                      {row.hasPublicAPI && <span className="pill draft">public API</span>}
                    </div>
                  </div>
                  {row.key === "er" ? (
                    <span className="pill live" title="EntryRate native — always on">Native · always on</span>
                  ) : row.status === "connected" ? (
                    <button className="btn" disabled={busy===row.key} onClick={()=>doDisconnect(row)}>
                      {busy===row.key ? "…" : "Disconnect"}
                    </button>
                  ) : (
                    <button className="btn primary" disabled={fields.length===0} onClick={()=>startConnect(row)}>
                      Connect
                    </button>
                  )}
                </div>

                {row.lastSyncAt && (
                  <div className="integ-foot">
                    Last sync · <b>{new Date(row.lastSyncAt).toLocaleString()}</b>
                    {row.eventCount != null && <> · <b>{row.eventCount}</b> events tracked</>}
                  </div>
                )}
                {row.lastError && <div className="integ-foot integ-err">{row.lastError}</div>}
                {row.why && (
                  <div className="integ-foot integ-why">
                    {row.why}
                    {row.plannedEndpoint && <pre className="integ-endpoint">{row.plannedEndpoint}</pre>}
                  </div>
                )}

                {isOpen && fields.length > 0 && (
                  <div className="integ-form">
                    {fields.map((f) => (
                      <label key={f.name} className="integ-field">
                        <span className="integ-lbl">{f.label}</span>
                        <input
                          type={f.secret ? "password" : "text"}
                          value={creds[f.name] || ""}
                          onChange={(e)=>setCreds({...creds, [f.name]: e.target.value})}
                          placeholder={f.help || ""}
                        />
                        {f.help && <span className="integ-help">{f.help}</span>}
                      </label>
                    ))}
                    <div className="integ-form-actions">
                      <button className="btn" onClick={()=>setOpenKey(null)}>Cancel</button>
                      <button className="btn primary" disabled={busy===row.key} onClick={()=>submitConnect(row)}>
                        {busy===row.key ? "Connecting…" : "Save & connect"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
